import {
  useCreateUnlimitedGoalsAllowed,
  useSortedGoals,
  useUpdateGoal,
} from 'features/goals';
import { useLocalizedLifeAreas, useUpdateLifeArea } from 'features/life-areas';
import { useUpdateUserSettings } from 'features/user';
import { useCallback } from 'react';
import { rootId } from 'shared/components/ui/virtualized-tree-list';
import { Goal, UpdateGoal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { lifeAreaOtherID } from 'shared/types/orderable-section';
import {
  GoalGroupBy,
  GoalSortingMode,
  GroupByMode,
} from 'shared/types/sorting-mode';
import {
  GoalRoadmapSortingKeys,
  UserSettings,
} from 'shared/types/user-settings';
import { getSubGoals } from 'shared/utils/get-sub-goals';
import { ARCHIVED_SECTION_ID } from 'shared/views/goal-filter-list';

export const useGoalTreeReorder = (groupBy: GoalGroupBy) => {
  const { submit: updateGoal } = useUpdateGoal();
  const { submit: updateLifeArea } = useUpdateLifeArea();
  const updateSettings = useUpdateUserSettings();
  const sortedGoals = useSortedGoals();
  const lifeAreas = useLocalizedLifeAreas();

  // needed for de-archiving logic
  const { allowed } = useCreateUnlimitedGoalsAllowed();

  return useCallback(
    (
      updatedSections: { id: ID; goalSorting: ID[] }[],
      updatedGoals: UpdateGoal[],
    ) => {
      const isLifeAreaGrouping = groupBy === GroupByMode.LifeArea;
      // Set user settings update base
      const settingsToUpdate: Partial<UserSettings> = {
        goalSortingMode: GoalSortingMode.Custom,
      };

      const goalsMap = sortedGoals.reduce<Record<ID, Goal>>((acc, goal) => {
        acc[goal.id] = goal;
        return acc;
      }, {});

      const lifeAreaMap = lifeAreas.reduce<Record<ID, LifeArea>>(
        (acc, lifeArea) => {
          acc[lifeArea.id] = lifeArea;
          return acc;
        },
        {},
      );

      const isArchivedSectionUpdated = updatedSections.some(
        ({ id }) => id === ARCHIVED_SECTION_ID,
      );

      // Execute collected updates
      updatedSections.forEach(({ id, goalSorting }) => {
        if (id !== ARCHIVED_SECTION_ID && isArchivedSectionUpdated) {
          goalSorting.forEach((goalId) => {
            const goal = goalsMap[goalId];
            if (!goal || !goal.archivedAt || !allowed) {
              return;
            }

            const goalToUpdate = updatedGoals.find(({ id }) => id === goal.id);
            if (goalToUpdate) {
              goalToUpdate.archivedAt = null;
              return;
            }
            updatedGoals.push({
              id: goal.id,
              archivedAt: null,
            });
          });
        }

        if (id === rootId) {
          if (isLifeAreaGrouping) {
            settingsToUpdate.lifeAreaSorting = goalSorting;
          }
          if (!isLifeAreaGrouping) {
            settingsToUpdate.goalRoadmapSorting = {
              [GoalRoadmapSortingKeys.InProgress]: goalSorting,
            };
          }
          return;
        }

        if (id === ARCHIVED_SECTION_ID) {
          const directArchivedGoalIds = goalSorting.filter(
            (id) => goalsMap[id],
          );
          directArchivedGoalIds.forEach((goalId) => {
            const goal = goalsMap[goalId];
            if (!goal) {
              return;
            }
            if (!goal.archivedAt) {
              const goalToUpdate = updatedGoals.find(
                ({ id }) => id === goal.id,
              );
              if (goalToUpdate) {
                goalToUpdate.archivedAt = new Date();
              }
              if (!goalToUpdate) {
                updatedGoals.push({
                  id: goal.id,
                  archivedAt: new Date(),
                });
              }
            }
            const subGoals = getSubGoals(goalId, {
              goals: sortedGoals,
              recursive: true,
            });

            subGoals.forEach((subGoal) => {
              if (!subGoal.archivedAt) {
                const goalToUpdate = updatedGoals.find(
                  ({ id }) => id === subGoal.id,
                );
                if (goalToUpdate) {
                  goalToUpdate.archivedAt = new Date();
                }
                if (!goalToUpdate) {
                  updatedGoals.push({
                    id: subGoal.id,
                    archivedAt: new Date(),
                  });
                }
              }
            });
          });
        }

        // update life area sorting
        const lifeArea = lifeAreaMap[id];
        if (lifeArea) {
          updateLifeArea({ id, goalSorting });
          return;
        }

        // in case it is not a known life-area, we add it to the goalGroupSorting (so for the no-life-area section)
        settingsToUpdate.goalGroupSorting = {
          ...(settingsToUpdate?.goalGroupSorting ?? {}),
          lifeArea: {
            ...(settingsToUpdate.goalGroupSorting?.lifeArea ?? {}),
            sorting: {
              ...(settingsToUpdate.goalGroupSorting?.lifeArea?.sorting ?? {}),
              [id === lifeAreaOtherID ? 'null' : id]: goalSorting,
            },
          },
        };
      });

      updateSettings(settingsToUpdate);

      updatedGoals.forEach((goal) =>
        // todo: update the goal as batch
        updateGoal({
          ...goal,
          goalSortingMode: GoalSortingMode.Custom,
        }),
      );
    },
    [
      groupBy,
      sortedGoals,
      lifeAreas,
      updateSettings,
      allowed,
      updateLifeArea,
      updateGoal,
    ],
  );
};
